import { create } from 'zustand';
import type { AnnotationKind } from '../utils/annotations/primitives';

/** 2026-10-17（标绘层 S2/S3）：绘制图元（= AnnotationKind；本类型别名避免与 sceneStore 形成运行期依赖） */
export type AnnotationDrawKind = AnnotationKind;

/** P1.5 G-05：记忆上次打开的大类/子类（跨刷新） */
const PICKER_LAST_KEY = 'dtwin-widget-picker-last';
function readPickerLast(): { group: string; subGroup: string } {
  try {
    if (typeof localStorage === 'undefined') return { group: 'all', subGroup: '' };
    const raw = localStorage.getItem(PICKER_LAST_KEY);
    const obj = raw ? JSON.parse(raw) : null;
    return {
      group: typeof obj?.group === 'string' ? obj.group : 'all',
      subGroup: typeof obj?.subGroup === 'string' ? obj.subGroup : '',
    };
  } catch {
    return { group: 'all', subGroup: '' };
  }
}
function writePickerLast(group: string, subGroup: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(PICKER_LAST_KEY, JSON.stringify({ group, subGroup }));
  } catch {
    /* 忽略存储失败 */
  }
}

/** 2026-09-14：拖拽步长（TransformControls 吸附）设置项——移动(米)/旋转(度)，0=不吸附（连续拖动） */
const TRANSFORM_SNAP_KEY = 'dtwin-transform-snap';
function readTransformSnap(): { moveSnap: number; rotateSnap: number; dragScale: number } {
  try {
    if (typeof localStorage === 'undefined') return { moveSnap: 0, rotateSnap: 0, dragScale: 1 };
    const raw = localStorage.getItem(TRANSFORM_SNAP_KEY);
    const obj = raw ? JSON.parse(raw) : null;
    return {
      moveSnap: typeof obj?.moveSnap === 'number' && obj.moveSnap >= 0 ? obj.moveSnap : 0,
      rotateSnap: typeof obj?.rotateSnap === 'number' && obj.rotateSnap >= 0 ? obj.rotateSnap : 0,
      dragScale: typeof obj?.dragScale === 'number' && obj.dragScale > 0 ? obj.dragScale : 1,
    };
  } catch {
    return { moveSnap: 0, rotateSnap: 0, dragScale: 1 };
  }
}
function writeTransformSnap(moveSnap: number, rotateSnap: number, dragScale: number): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(TRANSFORM_SNAP_KEY, JSON.stringify({ moveSnap, rotateSnap, dragScale }));
  } catch {
    /* 忽略存储失败 */
  }
}

/** 2026-09-14：悬停高亮开关（方案A 抑制组合之一——可整体关闭悬停染色；默认开） */
const HOVER_HIGHLIGHT_KEY = 'dtwin-hover-highlight';
function readHoverHighlight(): boolean {
  try {
    if (typeof localStorage === 'undefined') return true;
    const raw = localStorage.getItem(HOVER_HIGHLIGHT_KEY);
    return raw === null ? true : raw === '1';
  } catch {
    return true;
  }
}
function writeHoverHighlight(v: boolean): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(HOVER_HIGHLIGHT_KEY, v ? '1' : '0');
  } catch {
    /* 忽略存储失败 */
  }
}

/** 2026-09-14：网格格子偏好（自动=随相机距离档位 1/2/5×10ⁿ；手动=固定值）——对齐 Blender「Scale/Subdivisions 可调」 */
const GRID_PREF_KEY = 'dtwin-grid-prefs';
function readGridPrefs(): { gridAuto: boolean; gridCellSize: number } {
  try {
    if (typeof localStorage === 'undefined') return { gridAuto: true, gridCellSize: 10 };
    const raw = localStorage.getItem(GRID_PREF_KEY);
    const obj = raw ? JSON.parse(raw) : null;
    return {
      gridAuto: obj?.gridAuto !== false,
      gridCellSize: typeof obj?.gridCellSize === 'number' && obj.gridCellSize > 0 ? obj.gridCellSize : 10,
    };
  } catch {
    return { gridAuto: true, gridCellSize: 10 };
  }
}
function writeGridPrefs(gridAuto: boolean, gridCellSize: number): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(GRID_PREF_KEY, JSON.stringify({ gridAuto, gridCellSize }));
  } catch {
    /* 忽略存储失败 */
  }
}

interface EditorUiState {
  /** 左侧属性面板定位（scene / gis / layers 等） */
  activePanel: string | null;
  /** 2026-10-17（标绘层 S2/S3）：当前绘制模式（null=非绘制态）；完成/取消后置空 */
  annotationDrawKind: AnnotationDrawKind | null;
  /** 2026-10-17（标绘层 S3）：绘制落点方式（地面 / 模型表面射线） */
  annotationPlaneMode: 'ground' | 'model';
  /** 2026-10-17（标绘层 S3）：绘制吸附（就近吸附模型包围盒顶点/中心） */
  annotationSnap: boolean;
  /** 进入 GIS 卫星地图前左侧属性所在的定位（用于退出时恢复） */
  panelBeforeGis: string | null;
  /** 编辑器激活态互斥（G9/阶段1）：同一时刻只有一个编辑器上下文可操作
   *  - 'scene'：3D 场景编辑（默认），2D 大屏覆盖层只读
   *  - 'dashboard'：2D 大屏编辑（激活），3D 全部工具锁定（interactionLocked）
   */
  activeEditor: 'scene' | 'dashboard';
  /** 进入大屏编辑前左侧属性所在定位（用于退出时恢复，复用 panelBeforeGis 先例） */
  panelBeforeDashboard: string | null;
  /** 当前场景关联的大屏 pageId（阶段1.5｜G10：图层树共享同一关联状态；SceneEditor/LayerPanel 共用） */
  dashboardId: string | null;
  /** 关联/新建大屏弹窗（LayerPanel「2D 大屏图层」点击未关联时触发；SceneEditor 右上「大屏」按钮共用） */
  dashboardBindOpen: boolean;
  setDashboardBindOpen: (v: boolean) => void;
  /** 只读预览叠加（阶段1.5｜G10 收尾）：scene 态下点击大屏条目 → 3D 上叠加 2D 大屏合成预览（可交互查看，不进编辑态）；
   *  仅 scene 态有效；进入 dashboard 编辑态强制关闭（互斥） */
  overlayPreview: boolean;
  /** P1.5 两态组件面板：态 B（组件选择大面板）开关与筛选状态 */
  widgetPickerOpen: boolean;
  widgetPickerGroup: string;
  widgetPickerSubGroup: string;
  widgetPickerSearch: string;
  /** 面板拖拽中（面板避让：pointer-events 关闭，避免遮挡画布投放） */
  panelDragging: boolean;
  /** B2：3D 视口内组件就地编辑态（scene 态下叠加可交互编辑层；与 activeEditor='dashboard' 互斥） */
  overlayEditMode: boolean;
  /** B2：按住 Alt 临时交还 3D 相机（编辑层 pointer-events 关闭，3D 可拖动视角） */
  overlayAltCamera: boolean;
  /** 2026-10-05：大屏叠加编辑右侧属性区显示「数据绑定」而非组件属性 */
  overlay2dBinding: boolean;
  /** 2026-10-05：大屏叠加编辑右侧 Tab（属性配置 / 图层[组件层级]，同 2D 编辑器） */
  overlayRightTab: 'props' | 'layers';
  /** 2026-10-05：图层面板大屏图层卡片高亮闪烁（融合菜单「图层融合设置」定位用；值=时间戳） */
  layerCardHighlightAt: number;
  /** 2026-10-06：预览弹窗打开时暂停主画布渲染（双画布同帧导致 2fps“卡死”） */
  scenePreviewOpen: boolean;
  setScenePreviewOpen: (v: boolean) => void;
  setOverlayEditMode: (v: boolean) => void;
  setOverlayAltCamera: (v: boolean) => void;
  setOverlay2dBinding: (v: boolean) => void;
  setOverlayRightTab: (t: 'props' | 'layers') => void;
  highlightLayerCard: () => void;
  openWidgetPicker: (group?: string, subGroup?: string) => void;
  closeWidgetPicker: () => void;
  setWidgetPickerGroup: (group: string) => void;
  setWidgetPickerSubGroup: (subGroup: string) => void;
  setWidgetPickerSearch: (search: string) => void;
  setPanelDragging: (v: boolean) => void;
  setActivePanel: (panel: string | null) => void;
  setAnnotationDrawKind: (k: AnnotationDrawKind | null) => void;
  setAnnotationPlaneMode: (m: 'ground' | 'model') => void;
  setAnnotationSnap: (v: boolean) => void;
  setPanelBeforeGis: (panel: string | null) => void;
  setDashboardId: (id: string | null) => void;
  setOverlayPreview: (v: boolean) => void;
  /** 激活 2D 大屏编辑：锁 3D */
  /** 2026-09-14：拖拽步长（移动 米 / 旋转 度；0=不吸附） */
  moveSnap: number;
  rotateSnap: number;
  /** 2026-09-14：拖动轴锁（'x'|'y'|'z'|'yz'|'xz'|'xy'|null）——按钮/快捷键共用（按钮=持久开关） */
  axisLock: string | null;
  setAxisLock: (v: string | null) => void;
  /** 2026-09-14：拖拽幅度系数（1=正常；<1 微调 >1 粗调） */
  dragScale: number;
  /** 2026-09-14：悬停高亮开关（默认开；关闭后光标悬停不染色） */
  hoverHighlight: boolean;
  /** 2026-09-14：网格格子自动（随相机距离档位）/手动（固定值） */
  gridAuto: boolean;
  gridCellSize: number;
  setMoveSnap: (v: number) => void;
  setRotateSnap: (v: number) => void;
  setDragScale: (v: number) => void;
  setHoverHighlight: (v: boolean) => void;
  setGridAuto: (v: boolean) => void;
  setGridCellSize: (v: number) => void;
  enterDashboardEditor: () => void;
  /** 返回 3D 场景编辑：锁 2D（大屏回只读叠加） */
  enterSceneEditor: () => void;
}

const useEditorUiStore = create<EditorUiState>((set, get) => ({
  activePanel: null,
  annotationDrawKind: null,
  annotationPlaneMode: 'ground',
  annotationSnap: false,
  panelBeforeGis: null,
  activeEditor: 'scene',
  panelBeforeDashboard: null,
  dashboardId: null,
  dashboardBindOpen: false,
  overlayPreview: false,
  widgetPickerOpen: false,
  overlay2dBinding: false,
  overlayRightTab: 'props',
  layerCardHighlightAt: 0,
  scenePreviewOpen: false,
  widgetPickerGroup: readPickerLast().group,
  widgetPickerSubGroup: readPickerLast().subGroup,
  widgetPickerSearch: '',
  panelDragging: false,
  overlayEditMode: false,
  overlayAltCamera: false,
  moveSnap: readTransformSnap().moveSnap,
  rotateSnap: readTransformSnap().rotateSnap,
  dragScale: readTransformSnap().dragScale,
  hoverHighlight: readHoverHighlight(),
  gridAuto: readGridPrefs().gridAuto,
  gridCellSize: readGridPrefs().gridCellSize,
  axisLock: null,
  setAxisLock: (v) => set({ axisLock: v }),
  setMoveSnap: (v) => {
    const safe = Number.isFinite(v) && v >= 0 ? v : 0;
    set({ moveSnap: safe });
    writeTransformSnap(safe, get().rotateSnap, get().dragScale);
  },
  setRotateSnap: (v) => {
    const safe = Number.isFinite(v) && v >= 0 ? v : 0;
    set({ rotateSnap: safe });
    writeTransformSnap(get().moveSnap, safe, get().dragScale);
  },
  setDragScale: (v) => {
    const safe = Number.isFinite(v) && v > 0 ? Math.min(v, 10) : 1;
    set({ dragScale: safe });
    writeTransformSnap(get().moveSnap, get().rotateSnap, safe);
  },
  setHoverHighlight: (v) => {
    set({ hoverHighlight: v });
    writeHoverHighlight(v);
  },
  setGridAuto: (v) => {
    set({ gridAuto: v });
    writeGridPrefs(v, get().gridCellSize);
  },
  setGridCellSize: (v) => {
    const safe = Number.isFinite(v) && v > 0 ? Math.min(v, 1e6) : 10;
    set({ gridCellSize: safe });
    writeGridPrefs(get().gridAuto, safe);
  },
  setOverlayEditMode: (v) =>
    set((s) => ({
      overlayEditMode: v,
      // 进入大屏叠加编辑：关闭只读预览；左侧详情区切到「图层」（大屏图层卡片 + 2D 组件树）
      overlayPreview: v ? false : s.overlayPreview,
      overlayAltCamera: false,
      overlay2dBinding: false,
      activePanel: v ? 'layers' : s.activePanel,
    })),
  setOverlayAltCamera: (v) => set({ overlayAltCamera: v }),
  setOverlay2dBinding: (v) => set({ overlay2dBinding: v }),
  setOverlayRightTab: (t) => set({ overlayRightTab: t }),
  highlightLayerCard: () => set({ layerCardHighlightAt: Date.now() }),
  setScenePreviewOpen: (v) => set({ scenePreviewOpen: v }),
  openWidgetPicker: (group, subGroup) =>
    set((s) => {
      const g = group ?? (s.widgetPickerGroup || 'all');
      const nextSub = group && group !== s.widgetPickerGroup ? '' : subGroup ?? s.widgetPickerSubGroup;
      writePickerLast(g, nextSub);
      return { widgetPickerOpen: true, widgetPickerGroup: g, widgetPickerSubGroup: nextSub };
    }),
  closeWidgetPicker: () => set({ widgetPickerOpen: false, widgetPickerSearch: '' }),
  setWidgetPickerGroup: (group) => {
    set({ widgetPickerGroup: group, widgetPickerSubGroup: '' });
    writePickerLast(group, '');
  },
  setWidgetPickerSubGroup: (subGroup) => {
    set({ widgetPickerSubGroup: subGroup });
    writePickerLast(get().widgetPickerGroup, subGroup);
  },
  setWidgetPickerSearch: (search) => set({ widgetPickerSearch: search }),
  setPanelDragging: (v) => set({ panelDragging: v }),
  setActivePanel: (panel) => set({ activePanel: panel }),
  setAnnotationDrawKind: (k) => set({ annotationDrawKind: k }),
  setAnnotationPlaneMode: (m) => set({ annotationPlaneMode: m }),
  setAnnotationSnap: (v) => set({ annotationSnap: v }),
  setPanelBeforeGis: (panel) => set({ panelBeforeGis: panel }),
  setDashboardId: (id) => set({ dashboardId: id, overlayPreview: false }),
  setDashboardBindOpen: (v) => set({ dashboardBindOpen: v }),
  setOverlayPreview: (v) =>
    set((s) => ({
      overlayPreview: v,
      // 2026-10-11：打开只读叠加预览时退出大屏叠加编辑（互斥）——否则编辑层与只读层同时渲染，
      // 且只读层用的是页面快照 components，会出现「同一组件在原位与拖拽后位置各一份」的克隆重影
      overlayEditMode: v ? false : s.overlayEditMode,
      overlayAltCamera: v ? false : s.overlayAltCamera,
    })),
  enterDashboardEditor: () => {
    // 已在大屏编辑则幂等
    if (get().activeEditor === 'dashboard') return;
    const before = get().activePanel;
    set({
      activeEditor: 'dashboard',
      panelBeforeDashboard: before !== 'dashboard' ? before : null,
      activePanel: null,
      overlayPreview: false,
      overlayEditMode: false,
      overlayAltCamera: false,
    });
  },
  enterSceneEditor: () => {
    if (get().activeEditor === 'scene') return;
    set((s) => ({
      activeEditor: 'scene',
      activePanel: s.panelBeforeDashboard,
      panelBeforeDashboard: null,
    }));
  },
}));

export default useEditorUiStore;